'use client';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import type { Recipe } from '@roomtreatment/av-catalog';
import { useInViewOnce } from '@/lib/useInViewOnce';
import { PRODUCT_HERO_STAGE_CLASS } from './stage';

interface Props {
  recipe: Recipe;
  /** Accessible name of the model, e.g. "3D model of the ELAC DF63". */
  label: string;
  /** The committed static render, when the owner has generated it. */
  posterSrc: string | null;
}

/** What stands in the stage until three.js loads: the static render, or the
 *  empty white stage at the same size, so the swap moves nothing. */
function Poster({ label, posterSrc }: Pick<Props, 'label' | 'posterSrc'>) {
  return (
    <div className={PRODUCT_HERO_STAGE_CLASS} data-testid="gear-model-poster">
      {posterSrc && (
        // The lead picture on the product page (the hero's 20-24rem column
        // from md up, full width on phone) — priority since it's the page's
        // likely LCP image, matching the raw <img>'s previous eager load.
        <Image
          src={posterSrc}
          alt={label}
          width={512}
          height={512}
          sizes="(min-width: 768px) 24rem, 100vw"
          priority
          className="h-full w-full object-contain p-6"
        />
      )}
    </div>
  );
}

/**
 * The product's 3D model, orbitable. three.js is most of a gear page's
 * JavaScript, so the chunk is fetched only once the stage nears the viewport
 * (the room mode explorer's lazy heatmap precedent, plan S6).
 */
export default function GearModelViewer({ recipe, label, posterSrc }: Props) {
  const [ref, near] = useInViewOnce<HTMLDivElement>();
  return (
    <div ref={ref}>
      {near ? <LazyCanvas recipe={recipe} label={label} posterSrc={posterSrc} /> : <Poster label={label} posterSrc={posterSrc} />}
    </div>
  );
}

const Canvas = dynamic(() => import('./GearModelCanvas'), { ssr: false, loading: () => null });

function LazyCanvas({ recipe, label, posterSrc }: Props) {
  // `loading` cannot take props, so the poster is layered under the canvas
  // box instead: the canvas mounts over it in the same grid cell.
  return (
    <div className="grid [&>*]:[grid-area:1/1]">
      <Poster label={label} posterSrc={posterSrc} />
      <Canvas recipe={recipe} label={label} />
    </div>
  );
}
